import type { BetSelection, RaceSelection } from "@/features/betslip/types";
import { getIsSRMAvailable } from "@/utilities/racingUtilities";

// Race markets an SRM leg can be built from, matched by display name.
const srmMarketNames = ["Win", "Top 2", "Top 3", "Top 4"] as const;
type SrmMarketName = (typeof srmMarketNames)[number];

const isSrmMarket = (name: string): name is SrmMarketName =>
  (srmMarketNames as readonly string[]).includes(name);

export type SrmCandidate = {
  eventId: string;
  // The slip's singles, in slip order, that would become the SRM legs.
  legs: RaceSelection[];
  // Passes every state-derived gate for a real SRM: 2–4 distinct runners on
  // live markets, within the position caps. The suggestion hook adds the one
  // thing state can't settle — whether the server will price this exact set —
  // to reach "show the card". A set that fails these gates, or that the server
  // won't price, shows nothing.
  meetsCreateGates: boolean;
};

// A candidate exists only when EVERY selection in the slip is an eligible
// same-race single. A mixed slip — another race, a sport single, an exotic, an
// existing SEM, or a Starting Price — yields null and shows no card. This is
// the new-betslip port of the legacy selectIsAlmostSRM / selectCanBeSRM pair.
export const getSrmCandidate = (
  selections: BetSelection[],
  // Selections the slip can bet - trading market, and live only with the
  // permission. Derived in the store, since only it holds both.
  bettable: Readonly<Record<string, boolean>>,
): SrmCandidate | null => {
  if (selections.length < 2) return null;

  const legs: RaceSelection[] = [];
  for (const selection of selections) {
    if (selection.kind !== "race") return null;
    if (selection.isSP) return null;
    if (!isSrmMarket(selection.marketName)) return null;
    legs.push(selection);
  }

  const eventId = legs[0].eventId;
  if (legs.some((leg) => leg.eventId !== eventId)) return null;

  return {
    eventId,
    legs,
    meetsCreateGates: meetsCreateGates(legs, bettable),
  };
};

const meetsCreateGates = (
  legs: RaceSelection[],
  bettable: Readonly<Record<string, boolean>>,
): boolean => {
  if (legs.length < 2 || legs.length > 4) return false;

  // The event has to be offering SRM right now — same timing gate as the race
  // page (server semAvailableAtMs, else the sport/region window).
  const base = legs[0];
  if (
    !getIsSRMAvailable({
      scheduledStartTime: base.startTime,
      raceTimezone: base.timezone ?? null,
      sport: base.sport,
      semAvailableAtMs: base.semAvailableAtMs,
    })
  ) {
    return false;
  }

  // Every leg still bettable, by this user.
  if (!legs.every((leg) => bettable[leg.outcomeId])) {
    return false;
  }

  // Distinct runners — the same runner can't fill two legs.
  const names = new Set(legs.map((leg) => leg.selectionName));
  if (names.size !== legs.length) return false;

  // Position caps: at most 1 Win, 2 across the top-2 pool, 3 across top-3,
  // 4 across top-4 (a higher pool subsumes the lower ones).
  const counts: Record<SrmMarketName, number> = {
    Win: 0,
    "Top 2": 0,
    "Top 3": 0,
    "Top 4": 0,
  };
  for (const leg of legs) {
    if (isSrmMarket(leg.marketName)) counts[leg.marketName] += 1;
  }
  if (counts["Win"] > 1) return false;
  if (counts["Top 2"] + counts["Win"] > 2) return false;
  if (counts["Top 3"] + counts["Top 2"] + counts["Win"] > 3) return false;
  if (counts["Top 4"] + counts["Top 3"] + counts["Top 2"] + counts["Win"] > 4) {
    return false;
  }

  return true;
};
